import { doesProductComponentMatch } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useMemo } from 'react';
import { useCardCategoryContext } from '../CardCategoryProvider';
import type { PropAsset, PropContent, PropPositionOverride, PropsPayload } from './propValues.types';

const isPropContent = (value: unknown): value is PropContent => {
  if (!value) {
    return false;
  }

  if (typeof value !== 'object') {
    return false;
  }

  const content = value as PropContent;

  if (!Object.hasOwn(value, 'type')) {
    return false;
  }

  if (content.type !== 'image' && content.type !== 'video') {
    return false;
  }

  if (!Object.hasOwn(value, 'responsiveTarget')) {
    return false;
  }

  if (content.responsiveTarget !== 'desktop' && content.responsiveTarget !== 'mobile') {
    return false;
  }

  if (content.type === 'image') {
    if (!Object.hasOwn(value, 'src') || typeof content.src !== 'string') {
      return false;
    }
  } else if (content.type === 'video') {
    if (!Object.hasOwn(value, 'videoUrl') || typeof content.videoUrl !== 'string') {
      return false;
    }

    const noThumbnail = !Object.hasOwn(value, 'videoThumbnailUrl') || typeof content.videoThumbnailUrl !== 'string';
    const noBackgroundColor = !Object.hasOwn(value, 'backgroundColor') || typeof content.backgroundColor !== 'string';

    if (noThumbnail && noBackgroundColor) {
      return false;
    }
  }

  return true;
};

const isValidPositionOverride = (value: unknown): value is PropPositionOverride => {
  if (!value) {
    return false;
  }

  if (typeof value !== 'object') {
    return false;
  }

  const asPosition = value as PropPositionOverride;
  if (!Object.hasOwn(value, 'row')) {
    return false;
  }

  if (typeof asPosition.row !== 'number') {
    return false;
  }

  if (!Object.hasOwn(value, 'column')) {
    return false;
  }

  if (typeof asPosition.column !== 'number') {
    return false;
  }

  return true;
};

const isValidProp = (value: unknown): value is PropAsset => {
  if (!value) {
    return false;
  }

  if (typeof value !== 'object') {
    return false;
  }

  const asset = value as PropAsset;

  if (Object.hasOwn(value, 'label')) {
    if (typeof asset.label !== 'string') {
      return false;
    }
    if (!Object.hasOwn(value, 'link') || typeof asset.link !== 'string') {
      return false;
    }
  }

  if (!Object.hasOwn(value, 'categoryList') && typeof asset.categoryList !== 'string') {
    return false;
  }

  if (!Object.hasOwn(value, 'contentList')) {
    return false;
  }

  if (!Array.isArray(asset.contentList)) {
    return false;
  }

  if (asset.contentList.some(content => !isPropContent(content))) {
    return false;
  }

  if (Object.hasOwn(value, 'preferredPosition')) {
    if (!isValidPositionOverride(asset.preferredPosition)) {
      return false;
    }
  }

  return true;
};

const isValidPropsPayload = (payload: unknown): payload is PropsPayload => {
  if (!payload) {
    return false;
  }

  if (typeof payload !== 'object') {
    return false;
  }

  const asPayload = payload as PropsPayload;

  if (!asPayload.props) {
    return false;
  }

  if (asPayload.props.some(el => !isValidProp(el))) {
    return false;
  }

  return true;
};

const cardCategoryMatches = (categoriesList: string[], context: { occasion: string; category: StationeryTemplateCategoryEnum }) => {
  return categoriesList.some(category => {
    const categoryComponents = category.split('/');
    const [targetOccasion, targetCategory] = categoryComponents;
    const { category: currentCategory, occasion: currentOccasion } = context;

    if (!doesProductComponentMatch(targetOccasion, currentOccasion)) {
      return false;
    }

    if (!doesProductComponentMatch(targetCategory, currentCategory)) {
      return false;
    }

    return true;
  });
};

/**
 * Fetches and validates the printValuePropsData feature flag payload.
 * Returns useNewProps flag and validated payload (undefined when flag is off or payload invalid).
 */
export const useValuePropsPayload = () => {
  const { value: useNewProps, payload: newProps } = useFeatureValue('printValuePropsData');
  const { currentCategory: category } = useCardCategoryContext();

  return useMemo(() => {
    const validatedPayload = useNewProps && isValidPropsPayload(newProps) ? newProps : undefined;
    if (!validatedPayload) return undefined;
    return {
      ...validatedPayload,
      props: validatedPayload.props.filter(card => {
        return cardCategoryMatches(card.categoryList, {
          occasion: 'wedding',
          category,
        });
      }),
    };
  }, [useNewProps, newProps, category]);
};
